import React, { useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import MenuItem from "@mui/material/MenuItem";
import { useModalContext } from "../../../contexts/ModalContext";
import { Container, FormContainer } from "./styles";
import * as ClientPointsService from "../../../services/clientPoints";
import ConfirmationModal from "../../../components/ui/ConfirmationModal";
import DataTable from "react-data-table-component";
import "react-data-table-component-extensions/dist/index.css";

var moment = require("moment");

const CHANGE_TYPE_META = {
    CREATED: { label: "Criado", color: "info" },
    UPDATED: { label: "Atualizado", color: "primary" },
    DELETED: { label: "Removido", color: "default" },
    EARNED: { label: "Pontuado", color: "success" },
    REDEEMED: { label: "Resgatado", color: "secondary" },
    REDEMPTION_CODE_GENERATED: { label: "Código gerado", color: "warning" },
    REDEMPTION_FAILED: { label: "Resgate recusado", color: "error" },
};

function normalizePageResponse(response) {
    if (Array.isArray(response)) {
        return { content: response, totalElements: response.length };
    }
    return {
        content: Array.isArray(response?.content) ? response.content : [],
        totalElements: response?.totalElements || 0,
    };
}

function getLogMessage(row) {
    if (row.message) return row.message;
    if (row.changeType === "REDEMPTION_FAILED") {
        return "Nao e possivel usar pontos em uma ordem de servico que ja possui desconto.";
    }
    return "—";
}

const ClientPointsModal = ({ client, mechanicId, onUpdate }) => {
    const { setModal } = useModalContext();
    const [loading, setLoading] = useState(true);
    const [existingPoints, setExistingPoints] = useState(null);
    const [points, setPoints] = useState("");
    const [confirmationModal, setConfirmationModal] = useState({ open: false, choice: "remove" });
    const [logData, setLogData] = useState([]);
    const [logLoading, setLogLoading] = useState(false);
    const [logTotalRows, setLogTotalRows] = useState(0);
    const [logPage, setLogPage] = useState(0);
    const [logPerPage, setLogPerPage] = useState(5);
    const [logTypeFilter, setLogTypeFilter] = useState("");

    const handleClose = () => setModal(null);

    const fetchPoints = useCallback(async () => {
        setLoading(true);
        try {
            const data = await ClientPointsService.getPointsByMechanic(client.id, mechanicId);
            setExistingPoints(data);
            setPoints(data.points != null ? String(data.points) : "");
        } catch (err) {
            setExistingPoints(null);
            setPoints("");
        } finally {
            setLoading(false);
        }
    }, [client.id, mechanicId]);

    const fetchLogs = useCallback(async () => {
        setLogLoading(true);
        try {
            const response = await ClientPointsService.getLogsByUserAndMechanic(client.id, mechanicId, logPage, logPerPage);
            const normalized = normalizePageResponse(response);
            setLogData(normalized.content);
            setLogTotalRows(normalized.totalElements);
        } catch {
            setLogData([]);
            setLogTotalRows(0);
        } finally {
            setLogLoading(false);
        }
    }, [client.id, mechanicId, logPage, logPerPage]);

    useEffect(() => {
        fetchPoints();
    }, [fetchPoints]);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    const buildRequest = () => ({
        userId: client.id,
        mechanicId: mechanicId,
        points: Number(points),
    });

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (points === "") {
            alert("Informe a quantidade de pontos!");
            return;
        }
        if (Number.isNaN(Number(points)) || Number(points) < 0) {
            alert("Informe uma quantidade de pontos maior ou igual a zero.");
            return;
        }

        const request = buildRequest();

        if (existingPoints) {
            try {
                const data = await ClientPointsService.updatePoints(request);
                setExistingPoints(data);
                if (onUpdate) onUpdate();
                fetchLogs();
                alert("Pontos atualizados com sucesso!");
                return;
            } catch (err) {
                if (err.response && err.response.status === 404) {
                    // Record not found — fall through to create
                } else {
                    alert("Erro ao atualizar pontos!");
                    return;
                }
            }
        }

        try {
            const data = await ClientPointsService.createPoints(request);
            setExistingPoints(data);
            if (onUpdate) onUpdate();
            fetchLogs();
            alert("Pontos salvos com sucesso!");
        } catch {
            alert("Erro ao salvar pontos!");
        }
    };

    const handleDelete = async () => {
        if (existingPoints && existingPoints.points === 0) {
            setConfirmationModal({ open: false, choice: "none" });
            alert("Os pontos já estão zerados!");
            return;
        }
        try {
            await ClientPointsService.deletePoints(client.id, mechanicId);
            setExistingPoints(null);
            setPoints("");
            setConfirmationModal({ open: false, choice: "none" });
            if (onUpdate) onUpdate();
            fetchLogs();
            alert("Pontos removidos com sucesso!");
        } catch {
            alert("Erro ao remover pontos!");
        }
    };

    const filteredLogData = logTypeFilter ? logData.filter((row) => row.changeType === logTypeFilter) : logData;

    const logColumns = [
        {
            name: "Data",
            selector: (row) => row.createdAt ? moment(row.createdAt).format("DD/MM/YYYY HH:mm") : "—",
            minWidth: "150px",
        },
        {
            name: "Tipo",
            minWidth: "160px",
            cell: (row) => {
                const meta = CHANGE_TYPE_META[row.changeType] || { label: row.changeType || "—", color: "default" };
                return <Chip label={meta.label} size="small" color={meta.color} variant={meta.color === "default" ? "outlined" : "filled"} />;
            },
        },
        {
            name: "Pontos anteriores",
            selector: (row) => row.previousPoints,
            center: true,
            minWidth: "150px",
        },
        {
            name: "Pontos novos",
            selector: (row) => row.newPoints,
            center: true,
            minWidth: "130px",
        },
        {
            name: "Responsavel",
            selector: (row) => row.changedByEmail || "—",
            minWidth: "180px",
        },
        {
            name: "Mensagem",
            selector: (row) => getLogMessage(row),
            wrap: true,
            minWidth: "260px",
        },
    ];

    const logConditionalRowStyles = [
        {
            when: (row) => row.changeType === "REDEMPTION_FAILED",
            style: {
                backgroundColor: "#fff1f1",
                color: "#8a1f1f",
            },
        },
    ];

    return (
        <>
            <Modal open={true} onClose={handleClose}>
                <Container>
                    <Box
                        m={3}
                        fontWeight="bold"
                        color="#666666"
                        fontSize={{ xs: "h7.fontSize", sm: "h7.fontSize", md: "h6.fontSize", xl: "h5.fontSize" }}
                    >
                        Pontos - {client.name}
                    </Box>

                    {loading ? (
                        <Box display="flex" justifyContent="center" m={4}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            <form onSubmit={handleSubmit}>
                                <FormContainer>
                                    <TextField
                                        value={client.name}
                                        label="Cliente"
                                        InputProps={{ readOnly: true }}
                                        variant="outlined"
                                        fullWidth
                                    />
                                    <TextField
                                        value={client.email}
                                        label="Email"
                                        InputProps={{ readOnly: true }}
                                        variant="outlined"
                                        fullWidth
                                    />
                                    <TextField
                                        value={points}
                                        onChange={(e) => setPoints(e.target.value)}
                                        label="Pontos"
                                        type="number"
                                        variant="outlined"
                                        required
                                        fullWidth
                                        inputProps={{ min: 0 }}
                                    />

                                    {existingPoints && (
                                        <Typography variant="body2" color="text.secondary">
                                            Pontos atuais: <b>{existingPoints.points}</b>
                                        </Typography>
                                    )}

                                    <Box display="flex" justifyContent="flex-end" gap={1}>
                                        {existingPoints && (
                                            <Button
                                                variant="outlined"
                                                color="error"
                                                onClick={() => setConfirmationModal({ open: true, choice: "remove" })}
                                            >
                                                Excluir
                                            </Button>
                                        )}
                                        <Button variant="contained" type="submit">
                                            {existingPoints ? "Atualizar" : "Criar"}
                                        </Button>
                                        <Button variant="outlined" onClick={handleClose}>
                                            Fechar
                                        </Button>
                                    </Box>
                                </FormContainer>
                            </form>
                            <Box mx={3} mb={3}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
                                    <Typography variant="h6" color="#666666">
                                        Historico deste cliente
                                    </Typography>
                                    <TextField
                                        select
                                        size="small"
                                        label="Filtrar tipo"
                                        value={logTypeFilter}
                                        onChange={(event) => setLogTypeFilter(event.target.value)}
                                        sx={{ minWidth: 220 }}
                                    >
                                        <MenuItem value="">Todos</MenuItem>
                                        {Object.keys(CHANGE_TYPE_META).map((type) => (
                                            <MenuItem key={type} value={type}>
                                                {CHANGE_TYPE_META[type].label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Box>
                                <DataTable
                                    columns={logColumns}
                                    data={filteredLogData}
                                    pagination
                                    paginationServer
                                    paginationTotalRows={logTypeFilter ? filteredLogData.length : logTotalRows}
                                    onChangeRowsPerPage={(newPerPage) => setLogPerPage(newPerPage)}
                                    onChangePage={(newPage) => setLogPage(newPage - 1)}
                                    progressPending={logLoading}
                                    conditionalRowStyles={logConditionalRowStyles}
                                    persistTableHead
                                    noDataComponent="Nenhum registro encontrado"
                                />
                            </Box>
                        </>
                    )}
                </Container>
            </Modal>
            <ConfirmationModal
                open={confirmationModal.open}
                setOpen={setConfirmationModal}
                subText="Deseja mesmo remover os pontos deste cliente?"
                handleConfirmation={handleDelete}
            />
        </>
    );
};

export default ClientPointsModal;
